import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";
import { Download, RotateCw, WifiOff, PauseCircle, Inbox, FilterX } from "lucide-react";

import { Card } from "@/components/ui/Card";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import Select from "@/components/ui/Select";
import Skeleton from "@/components/shared/Skeleton";
import SearchInput from "@/components/shared/SearchInput";
import MultiSelectFilter from "@/components/shared/MultiSelectFilter";
import DateRangeFilter, { dateRangeParams } from "@/components/shared/DateRangeFilter";
import ListPagination from "@/components/shared/ListPagination";
import { Error as ErrorState } from "@/components/shared/Error";
import PermissionGuard from "@/permissions/PermissionGuard";
import ModuleUnavailableNotice from "@/permissions/ModuleUnavailableNotice";
import { MODULES } from "@/permissions/modules";
import { PERMISSIONS } from "@/permissions/constant";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { cn } from "@/lib/utils";

import { ComplianceService } from "@/services/complianceService";
import ActivityRow from "../components/ActivityRow";
import useComplianceLiveUpdates from "../hooks/useComplianceLiveUpdates";

/**
 * Compliance Audit Log — the tenant's own trail, in full.
 *
 * The dashboard's Live Activity Feed and this page read the same rows and
 * render them with the same `ActivityRow`. The feed is a recent digest: a
 * capped handful, narrowed in memory. This is the evidence view — filtered and
 * paged server-side, reaching all the way back.
 *
 * Gated on COMPLIANCE_AUDIT_VIEW rather than COMPLIANCE_VIEW. RLS scopes
 * `audit_logs` to the tenant and no further, so on a tenant that restricts
 * recruiter data visibility this page shows rows about candidates the reader
 * cannot otherwise reach. That is deliberate — an audit trail with rows removed
 * is weaker evidence — and the separate permission is what makes it a decision
 * an administrator takes rather than something every officer inherits.
 */

const ACTOR_TYPES = [
  { value: "", label: "All actors" },
  { value: "user", label: "Staff" },
  // The candidate acting through their own portal link. Worth its own filter:
  // "what did the candidate themselves do" is a question asked of every trail.
  { value: "candidate", label: "Candidate" },
  { value: "superadmin", label: "Platform admin" },
  { value: "system", label: "System" },
];

const PAGE_SIZE_DEFAULT = 20;

// Entity types read better as words than as the storage constants the API
// sends. Anything unmapped falls back to the raw value, so a newly-added type
// shows as itself rather than disappearing from the dropdown.
const ENTITY_LABELS = {
  COMPLIANCE_CANDIDATE: "Candidate",
  COMPLIANCE_DOCUMENT: "Document",
  COMPLIANCE_CHECKLIST: "Checklist",
  COMPLIANCE_SETTINGS: "Settings",
};

const ComplianceAuditLog = () => {
  const setHeader = useOutletContext();

  const [search, setSearch] = useState("");
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [actorType, setActorType] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ actions: [], entityTypes: [] });

  useEffect(() => {
    setHeader({ title: "Compliance Audit Log", subTitle: "Every recorded compliance action in your organisation — who did it, what changed, and when." });
    return () => setHeader({ title: "", subTitle: "" });
  }, [setHeader]);

  const filtersActive = Boolean(search || action || entityType || actorType || range.from || range.to);

  const params = useMemo(
    () => ({
      page,
      perPage: pageSize,
      ...(search && { search }),
      ...(action && { action }),
      ...(entityType && { entityType }),
      ...(actorType && { actorType }),
      ...dateRangeParams(range),
    }),
    [page, pageSize, search, action, entityType, actorType, range],
  );

  // `silent` skips the skeleton. A live refresh must not blank a trail someone
  // is reading mid-scroll; only a filter change or the first load shows it.
  const load = useCallback(
    (silent = false) => {
      let active = true;
      if (!silent) setLoading(true);
      setError("");

      ComplianceService.getAuditLog(params)
        .then((res) => {
          if (!active) return;
          setItems(res?.data?.items ?? []);
          setTotal(res?.data?.pagination?.total ?? 0);
        })
        .catch((err) => {
          if (!active) return;
          // An empty list on a failed request would assert that nothing ever
          // happened, which is the one thing an audit page must never say by
          // accident. On a silent refresh keep what is already on screen.
          if (!silent) {
            setItems([]);
            setTotal(0);
            setError(apiErrorMessageFormat(err) || "Could not load the audit log.");
          }
        })
        .finally(() => {
          if (active && !silent) setLoading(false);
        });

      return () => {
        active = false;
      };
    },
    [params],
  );

  useEffect(() => load(), [load]);

  useEffect(() => {
    ComplianceService.getAuditLogFilterOptions()
      .then((res) => setFilterOptions({ actions: res?.actions ?? [], entityTypes: res?.entityTypes ?? [] }))
      // A failed options fetch costs the dropdowns, not the page: the trail
      // still reads, and the filters simply offer nothing.
      .catch(() => setFilterOptions({ actions: [], entityTypes: [] }));
  }, []);

  // Any filter change puts the reader back on page 1. Staying on page 7 of a
  // result set that now has two pages shows an empty screen that looks like
  // "no matches" rather than "you are past the end".
  useEffect(() => {
    setPage(1);
  }, [search, action, entityType, actorType, range, pageSize]);

  // Live only on the unfiltered first page. Rows arriving under a reader who is
  // working through a filtered result would shift what they are looking at —
  // tolerable on an ordinary list, not on evidence. Newest-first ordering makes
  // it concrete: one new row on page 1 pushes every later page down by one, so
  // a reader on page 2 sees a row they already read reappear and one drop off
  // the bottom.
  //
  // The compliance domain's other paginated list (`intake/index.jsx`)
  // subscribes unconditionally and refetches the current page. Departing from
  // it is deliberate — that is a worklist, this is evidence — and the departure
  // is only honest because the header says which state the page is in and the
  // Refresh button is always there.
  //
  // One flag for both the hook and the header: they disagreed once, and the
  // page-2 reader got a silently frozen screen with no indicator.
  const livePaused = filtersActive || page > 1;
  const { stale } = useComplianceLiveUpdates({
    enabled: !livePaused,
    // The dedupe key carries the serialised params, so it cannot be named here.
    // `clearAll` is the established route for parameterised keys.
    clearAll: true,
    onRefresh: () => load(true),
  });

  const clearFilters = () => {
    setSearch("");
    setAction("");
    setEntityType("");
    setActorType("");
    setRange({ from: "", to: "" });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // The filters, without the paging: the file is what the screen is
      // showing, not the page the reader happens to be on.
      const { page: _page, perPage: _perPage, ...filters } = params;
      await ComplianceService.exportAuditLog(filters);
      toast.success("Audit log exported.");
    } catch (err) {
      toast.error(apiErrorMessageFormat(err) || "Could not export the audit log.");
    } finally {
      setExporting(false);
    }
  };

  const entityOptions = useMemo(() => filterOptions.entityTypes.map((t) => ENTITY_LABELS[t] ?? t), [filterOptions.entityTypes]);

  // The dropdown shows labels; the API takes the raw types. Mapping back here
  // keeps the request honest without the label table existing twice.
  const onEntityChange = (labels) => {
    const raw = (labels ? labels.split(",") : []).map((label) => filterOptions.entityTypes.find((t) => (ENTITY_LABELS[t] ?? t) === label) ?? label).filter(Boolean);
    setEntityType(raw.join(","));
  };

  const entityValue = useMemo(() => (entityType ? entityType.split(",") : []).map((t) => ENTITY_LABELS[t] ?? t).join(","), [entityType]);

  return (
    <PermissionGuard permission={PERMISSIONS.COMPLIANCE_AUDIT_VIEW}>
      <div className="flex flex-col gap-3">
        {/* This page writes nothing — the list and the export are both GETs, so
            a kill switch changes none of it. The notice still belongs: a reader
            treating this as a complete record needs to know that no new
            document has been taken in since the module was stopped. */}
        <ModuleUnavailableNotice module={MODULES.COMPLIANCE} suffix="Nothing on this page is affected — the trail and its export still work." />

        <div className="flex items-center justify-end gap-2 flex-wrap">
          {/* Three states, and the reader must be able to tell them apart.
              Paused is checked FIRST: while `enabled` is false the hook
              never subscribes, so `stale` stays false and a page-2 reader
              would otherwise see no indicator at all — a frozen screen
              claiming, by silence, to be current. */}
          {livePaused ? (
            <span className="flex items-center gap-1 text-[10.5px] text-text-secondary">
              <PauseCircle size={12} />
              {filtersActive ? "Paused while filtering" : "Paused while paging"}
            </span>
          ) : (
            stale && (
              <span className="flex items-center gap-1 text-[10.5px] text-text-secondary">
                <WifiOff size={12} />
                Not updating live
              </span>
            )
          )}

          <Button type="button" variant="tertiary" size="sm" className="rounded-lg" onClick={() => load()} disabled={loading}>
            <RotateCw size={13} className="mr-1" />
            Refresh
          </Button>
          <Button type="button" variant="secondary" size="sm" className="rounded-lg" onClick={handleExport} disabled={exporting || loading}>
            <Download size={13} className="mr-1" />
            {exporting ? "Exporting…" : "Export"}
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search actions…" className="max-w-xs" inputClassName="h-8 py-0" />
          <MultiSelectFilter label="Action" options={filterOptions.actions} value={action} onChange={setAction} />
          <MultiSelectFilter label="Type" options={entityOptions} value={entityValue} onChange={onEntityChange} />
          <div className="w-40">
            <Select value={actorType} onChange={setActorType} options={ACTOR_TYPES} placeholder="All actors" className="text-xs" />
          </div>
          <DateRangeFilter name="audit-log" from={range.from} to={range.to} onChange={setRange} />
          {filtersActive && (
            <Button type="button" variant="tertiary" size="sm" className="rounded-lg" onClick={clearFilters}>
              <FilterX size={13} className="mr-1" />
              Clear
            </Button>
          )}
        </div>

        <Card className="p-0 overflow-hidden">
          <div className={cn("px-4 py-1", (loading || (!error && items.length === 0)) && "min-h-[280px]")}>
            {loading ? (
              <div className="flex flex-col gap-2 py-3">
                {[...Array(8)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : error ? (
              <ErrorState error={error} onHandleRetry={() => load()} />
            ) : items.length === 0 ? (
              // Two different empty states, because they mean different things:
              // "nothing matches what you asked for" is recoverable by changing
              // the filters, "nothing has happened yet" is not.
              <div className="flex flex-col items-center justify-center py-16 gap-2 text-center">
                <div className="w-12 h-12 rounded-2xl bg-background-form flex items-center justify-center">
                  <Inbox size={22} className="text-text-primary/30" />
                </div>
                <p className="text-xs font-medium text-text-primary">{filtersActive ? "No events match these filters" : "No compliance activity recorded yet"}</p>
                <p className="text-[11px] text-text-secondary max-w-[300px]">
                  {filtersActive
                    ? "Try a wider date range, or clear the filters to see the whole trail."
                    : "Events appear here as candidates upload documents and officers review them."}
                </p>
                {filtersActive && (
                  <Button type="button" variant="tertiary" size="sm" onClick={clearFilters} className="mt-1 rounded-lg">
                    Clear filters
                  </Button>
                )}
              </div>
            ) : (
              items.map((item) => <ActivityRow key={item.id} item={item} showActor />)
            )}
          </div>

          {!loading && !error && total > 0 && (
            <div className="px-4">
              <ListPagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize} label="events" />
            </div>
          )}
        </Card>
      </div>
    </PermissionGuard>
  );
};

export default ComplianceAuditLog;
